import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, Users, UserCog, Briefcase, FileText, CreditCard,
  AlertTriangle, Image as ImageIcon, Settings, LogOut, Menu, X, BookOpen, FileCheck, Award, ClipboardList, Heart, Radio, GitBranch, GraduationCap, HandHeart,
} from "lucide-react";

const adminLinks = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/students", label: "Students", icon: Users },
  { to: "/admin/volunteers", label: "Volunteers", icon: UserCog },
  { to: "/admin/referral-tree", label: "Referral Tree", icon: GitBranch },
  { to: "/admin/internships", label: "Internships", icon: Briefcase },
  { to: "/admin/applications", label: "Applications", icon: ClipboardList },
  { to: "/admin/social-work", label: "Social Work", icon: HandHeart },
  { to: "/admin/social-work-applications", label: "Social Work Apps", icon: ClipboardList },
  { to: "/admin/test-series", label: "Test Series", icon: BookOpen },
  { to: "/admin/courses", label: "Courses", icon: GraduationCap },
  { to: "/admin/attempts", label: "Exam Attempts", icon: FileCheck },
  { to: "/admin/live-proctor", label: "Live Proctor", icon: Radio },
  { to: "/admin/cheating-logs", label: "Cheating Logs", icon: AlertTriangle },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/donations", label: "Donations", icon: Heart },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/ambassadors", label: "Ambassadors", icon: GraduationCap },
  { to: "/admin/homepage", label: "Homepage CMS", icon: Settings },
];

const studentLinks = [
  { to: "/student", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/student/internships", label: "Internships", icon: Briefcase },
  { to: "/student/applications", label: "My Applications", icon: ClipboardList },
  { to: "/student/social-work", label: "Social Work", icon: HandHeart },
  { to: "/student/test-series", label: "Test Series", icon: BookOpen },
  { to: "/student/courses", label: "Courses", icon: GraduationCap },
  { to: "/student/results", label: "My Results", icon: Award },
  { to: "/student/profile", label: "Profile", icon: UserCog },
];

const volunteerLinks = [
  { to: "/volunteer", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/volunteer/tasks", label: "Permissions", icon: ClipboardList },
];

const ambassadorLinks = [
  { to: "/ambassador", label: "Dashboard", icon: LayoutDashboard, end: true },
];

function studentLinksFor(user) {
  const interest = (user?.interest || "both").toLowerCase();
  return studentLinks.filter((l) => {
    if (interest === "internship" && (l.to === "/student/test-series" || l.to === "/student/results")) return false;
    if (interest === "test_series" && (l.to === "/student/internships" || l.to === "/student/applications" || l.to === "/student/social-work")) return false;
    return true;
  });
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const links =
    user?.role === "admin" ? adminLinks :
    user?.role === "volunteer" ? volunteerLinks :
    user?.role === "ambassador" ? ambassadorLinks :
    studentLinksFor(user);

  const titleByRole = {
    admin: "Admin Panel",
    volunteer: "Volunteer Panel",
    student: "Student Panel",
    ambassador: "Ambassador Panel",
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 fixed inset-y-0 left-0 z-30">
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-200">
          <Link to="/" data-testid="sidebar-brand-link">
            <Logo size={36} />
          </Link>
        </div>
        <div className="px-5 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{titleByRole[user?.role]}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-3">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-brand text-white" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
                data-testid={`sidebar-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              >
                <Icon size={18} /> {l.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-red-50 hover:text-red-600"
            data-testid="logout-button"
          >
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2" data-testid="mobile-sidebar-toggle">
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <Logo size={32} />
        <button onClick={handleLogout} className="p-2" aria-label="Logout" data-testid="mobile-logout-button">
          <LogOut size={18} />
        </button>
      </div>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-20 bg-black/50" onClick={() => setSidebarOpen(false)}>
          <aside className="bg-white w-64 h-full p-3 pt-16" onClick={(e) => e.stopPropagation()}>
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-sm font-medium ${
                      isActive ? "bg-brand text-white" : "text-slate-700"
                    }`
                  }
                >
                  <Icon size={18} /> {l.label}
                </NavLink>
              );
            })}
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
